import React, { useEffect, useMemo, useState } from "react";
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import {
    Anchor,
    Badge,
    Button,
    Card,
    Checkbox,
    Divider,
    Group,
    Loader,
    Modal,
    NumberInput,
    SegmentedControl,
    Stack,
    Text,
    Title,
} from "@mantine/core";
import { IconCalendar, IconKey, IconRefresh, IconShoppingCartPlus, IconTrash } from "@tabler/icons-react";
import { DateInput } from "@mantine/dates";
import "@mantine/dates/styles.css";
import { Alert, TextInput } from "@mantine/core";
import { InfoHint } from "../InfoHint/InfoHint";
import { LoadOrderDataAsync, OrderData } from "../OrderList/OrderList";
import { buildSampleOrders } from "../../scripts/SampleOrders";
import { STANDARD_FLAG, PRO_FLAG } from "../../Settings";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useCart } from "../Cart/CartContext";
import { useSession } from "../../auth/SessionContext";
import {
    Cart,
    CartLine,
    FULL_PRICE_PCS,
    getLineTotal,
    unitPrice,
} from "./UseCart";
import {
    Edition,
    Duration,
    LMUTV_PRICING,
    validUntilForDuration,
    formatValidUntil,
} from "../../Settings";
import i18n from "../../i18n";
import { DEBUG } from "../..";
import classes from "./Checkout.module.css";

// The one-time cart sells LIFETIME only — 3m/1y are recurring subscriptions and are
// routed to the subscription checkout (see CartContext.openCart). This stops anyone
// buying a time-limited tier as a one-off inside the cart.
const DURATIONS: Duration[] = ["lifetime"];
const EDITIONS: Edition[] = ["standard", "pro"];

const editionLabel = (e: Edition) =>
    e === "pro" ? "LMU TV Pro" : "LMU TV Standard";

interface CheckoutProps {
    logged_in: boolean;
}

interface CheckoutInnerProps extends CheckoutProps {
    isPending: boolean;
    onApproveNavigation: () => void;
    t: (key: string, opts?: any) => string;
    loadSessionDataAsync: () => Promise<boolean>;
    initialSelection: { edition: Edition; duration: Duration; pcs: number; startsAt?: string; autoAdd?: boolean };
}

interface CheckoutInnerState {
    cart: Cart;
    activeEdition: Edition;
    selections: Record<Edition, { duration: Duration; pcs: number; startsAt: string }>;
    orderID: string;
    onApproveMessage: string;
    onErrorMessage: string;
    showPreError: boolean;
    showPostError: boolean;
    tosAccepted: boolean;
    accTvKey: string;
    // Soonest-expiring active non-lifetime key per tier — drives the
    // "renew without overlap" suggestion under each EditionCard.
    renewalCandidate: Partial<Record<Edition, OrderData>>;
}

// Pick the active (validUntil > now) non-lifetime key for each tier whose
// expiry is closest in the future. Lifetime keys are skipped — there's
// nothing to extend. Refunded keys are skipped.
function pickRenewalCandidates(orders: OrderData[]): Partial<Record<Edition, OrderData>> {
    const now = Date.now();
    const out: Partial<Record<Edition, OrderData>> = {};
    const candidates: Array<{ edition: Edition; o: OrderData; expiry: number }> = [];
    for (const o of orders) {
        if (o.refunded) continue;
        if (!o.validUntil) continue; // lifetime
        const exp = new Date(o.validUntil).getTime();
        if (isNaN(exp) || exp <= now) continue; // already expired
        // Match by feature flag (with the temp Standard=1 / Pro=4 mapping).
        if ((o.features & PRO_FLAG) === PRO_FLAG) {
            candidates.push({ edition: "pro", o, expiry: exp });
        } else if ((o.features & STANDARD_FLAG) === STANDARD_FLAG) {
            candidates.push({ edition: "standard", o, expiry: exp });
        }
    }
    candidates.sort((a, b) => a.expiry - b.expiry);
    for (const c of candidates) {
        if (!out[c.edition]) out[c.edition] = c.o;
    }
    return out;
}

// ACC TV Lifetime owners can apply their key once for a 15% discount.
// Server-side single-use enforcement happens in process-order.php.
export const ACC_TV_DISCOUNT = 0.15;
const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
export const isValidAccTvKey = (k: string): boolean => UUID_RE.test(k.trim());

// "YYYY-MM-DD" for today, used as default for new lines.
const todayISO = (): string => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const parseISODate = (s?: string): Date => {
    if (!s) return new Date();
    const d = new Date(s + "T00:00:00");
    return isNaN(d.getTime()) ? new Date() : d;
};

class CheckoutInner extends React.Component<CheckoutInnerProps, CheckoutInnerState> {
    constructor(props: CheckoutInnerProps) {
        super(props);
        const cart = new Cart();
        const init = props.initialSelection;
        const initStarts = init.startsAt ?? todayISO();
        // When the wizard hands us a fully-specified selection it sets autoAdd —
        // seed the cart so the user lands on a populated summary, not the picker.
        if (init.autoAdd) {
            cart.AddLineItem(init.edition, init.duration, init.pcs, init.duration === "lifetime" ? undefined : initStarts);
        }
        this.state = {
            cart,
            activeEdition: init.edition,
            selections: {
                standard: {
                    duration: init.edition === "standard" ? init.duration : "lifetime",
                    pcs: init.edition === "standard" ? init.pcs : 1,
                    startsAt: init.edition === "standard" ? initStarts : todayISO(),
                },
                pro: {
                    duration: init.edition === "pro" ? init.duration : "lifetime",
                    pcs: init.edition === "pro" ? init.pcs : 1,
                    startsAt: init.edition === "pro" ? initStarts : todayISO(),
                },
            },
            orderID: "",
            onApproveMessage: "",
            onErrorMessage: "",
            showPreError: false,
            showPostError: false,
            tosAccepted: false,
            accTvKey: "",
            renewalCandidate: {},
        };
    }

    async componentDidMount() {
        // Fetch orders once on open so we can suggest a gap-free renewal
        // date when the buyer already owns an active non-lifetime key.
        try {
            const isDev = process.env.NODE_ENV === "development";
            const orders = isDev ? buildSampleOrders() : await LoadOrderDataAsync();
            if (Array.isArray(orders)) {
                this.setState({ renewalCandidate: pickRenewalCandidates(orders) });
            }
        } catch (e) {
            if (DEBUG) console.error("renewalCandidate load", e);
        }
    }

    discountedTotal(): number {
        const base = this.state.cart.Total();
        return isValidAccTvKey(this.state.accTvKey) ? base * (1 - ACC_TV_DISCOUNT) : base;
    }

    setEditionSelection(edition: Edition, patch: Partial<{ duration: Duration; pcs: number; startsAt: string }>) {
        this.setState((s) => ({
            selections: { ...s.selections, [edition]: { ...s.selections[edition], ...patch } },
        }));
    }

    addCurrentToCart = (edition: Edition) => {
        const sel = this.state.selections[edition];
        const cart = this.state.cart;
        cart.AddLineItem(
            edition,
            sel.duration,
            Math.max(1, sel.pcs),
            sel.duration === "lifetime" ? undefined : sel.startsAt
        );
        this.forceUpdate();
    };

    removeLine = (idx: number) => {
        const cart = this.state.cart;
        cart.RemoveLine(idx);
        this.forceUpdate();
    };

    updateLinePcs = (idx: number, pcs: number) => {
        const cart = this.state.cart;
        cart.SetLine(idx, pcs);
        this.forceUpdate();
    };

    buildPayPalItems(line: CartLine) {
        const u = unitPrice(line.edition, line.duration);
        const items: any[] = [];
        // PayPal item description doubles as a hint; the authoritative line metadata
        // (including startsAt) is sent separately as the `lines` JSON field.
        const description = `${line.edition}-${line.duration}`;
        const fullQty = Math.min(line.pcs, FULL_PRICE_PCS);
        if (fullQty > 0) {
            items.push({
                name: editionLabel(line.edition),
                description,
                category: "DIGITAL_GOODS",
                quantity: fullQty,
                unit_amount: { value: u.toFixed(2), currency_code: "EUR" },
            });
        }
        if (line.pcs > FULL_PRICE_PCS) {
            items.push({
                name: editionLabel(line.edition) + " (50% bulk)",
                description,
                category: "DIGITAL_GOODS",
                quantity: line.pcs - FULL_PRICE_PCS,
                unit_amount: { value: (u / 2).toFixed(2), currency_code: "EUR" },
            });
        }
        return items;
    }

    createOrder = (_data: Record<string, unknown>, actions: any) => {
        const ref = localStorage.getItem("ref") ?? "";
        const cart = this.state.cart;
        const allItems = cart.Items.flatMap((l) => this.buildPayPalItems(l));
        const itemTotal = cart.Total();
        const hasDiscount = isValidAccTvKey(this.state.accTvKey);
        const discount = hasDiscount ? itemTotal * ACC_TV_DISCOUNT : 0;
        const grand = itemTotal - discount;

        const breakdown: any = {
            item_total: { value: itemTotal.toFixed(2), currency_code: "EUR" },
        };
        if (discount > 0) {
            breakdown.discount = { value: discount.toFixed(2), currency_code: "EUR" };
        }

        return actions.order
            .create({
                purchase_units: [
                    {
                        amount: {
                            value: grand.toFixed(2),
                            breakdown,
                        },
                        items: allItems,
                    },
                ],
            })
            .then((orderID: any) => {
                this.setState({ orderID });
                return this.logOrder(ref, orderID).then((ok: boolean) => {
                    if (ok) return orderID;
                    this.setState({ showPreError: true });
                    return Promise.reject("Order failed");
                });
            });
    };

    async logOrder(ref: string, id: string): Promise<boolean> {
        const fd = new FormData();
        fd.append("amount", this.state.cart.Quantity().toString());
        fd.append("affiliate", ref);
        fd.append("id", id);
        fd.append("st", "CREATED");
        try {
            const r = await fetch("/dyn/lmutv/process-order.php?mode=create", { method: "POST", body: fd });
            if (!r.ok) throw new Error("network");
            const j = await r.json();
            return j.success === true;
        } catch (e: any) {
            if (DEBUG) console.error("logOrder", e);
            return false;
        }
    }

    async handleSubmit(ref: string, email: string, id: string, status: string): Promise<boolean> {
        const fd = new FormData();
        fd.append("amount", this.state.cart.Quantity().toString());
        fd.append("affiliate", ref);
        fd.append("em", email);
        fd.append("id", id);
        fd.append("st", status);
        // ACC TV Lifetime redemption key (single-use, 15% off). Backend validates + marks redeemed.
        if (isValidAccTvKey(this.state.accTvKey)) {
            fd.append("accTvKey", this.state.accTvKey.trim());
        }
        // Per-line metadata so process-order.php can issue keys with the right
        // features bitmask + validUntil per duration.
        fd.append(
            "lines",
            JSON.stringify(
                this.state.cart.Items.map((l) => ({
                    edition: l.edition,
                    duration: l.duration,
                    pcs: l.pcs,
                    startsAt: l.duration === "lifetime" ? null : (l.startsAt ?? todayISO()),
                }))
            )
        );
        try {
            const r = await fetch("/dyn/lmutv/process-order.php?mode=new", { method: "POST", body: fd });
            if (!r.ok) throw new Error("network");
            const j = await r.json();
            return j.success === true;
        } catch (e: any) {
            if (DEBUG) console.error("handleSubmit", e);
            return false;
        }
    }

    onApprove = (_data: any, actions: any) => {
        const app = this;
        return actions.order.capture().then(async function (details: any) {
            const ref = localStorage.getItem("ref") ?? "";
            const ok = await app.handleSubmit(ref, details.payer.email_address, details.id, details.status);
            if (ok) {
                app.setState({
                    onApproveMessage: `Transaction completed by ${details.payer.name?.given_name ?? "you"}!`,
                });
                app.props.onApproveNavigation();
            } else {
                app.setState({ showPostError: true });
            }
        });
    };

    onError = (err: Record<string, unknown>) => {
        this.setState({ onErrorMessage: err.toString() });
    };

    applyRenewalDate(edition: Edition) {
        const candidate = this.state.renewalCandidate[edition];
        if (!candidate?.validUntil) return;
        const exp = new Date(candidate.validUntil);
        const iso = `${exp.getFullYear()}-${String(exp.getMonth() + 1).padStart(2, "0")}-${String(exp.getDate()).padStart(2, "0")}`;
        // If the user has Lifetime selected, also flip to a sensible time-bound
        // duration — Lifetime + startsAt makes no sense.
        const patch: Partial<{ duration: Duration; pcs: number; startsAt: string }> = { startsAt: iso };
        if (this.state.selections[edition].duration === "lifetime") patch.duration = "1y";
        this.setEditionSelection(edition, patch);
    }

    renderEditionCard(edition: Edition) {
        const { t } = this.props;
        const sel = this.state.selections[edition];
        const u = unitPrice(edition, sel.duration);
        const linePreview = getLineTotal({ edition, duration: sel.duration, pcs: Math.max(1, sel.pcs) });
        const showBulk = sel.pcs > FULL_PRICE_PCS;
        const renewal = this.state.renewalCandidate[edition];
        const renewalExpiry = renewal?.validUntil ? new Date(renewal.validUntil) : null;
        const renewalDateStr = renewalExpiry
            ? formatValidUntil(renewalExpiry, i18n.language)
            : "";
        const startsAtMatchesRenewal =
            renewalExpiry !== null &&
            sel.startsAt === `${renewalExpiry.getFullYear()}-${String(renewalExpiry.getMonth() + 1).padStart(2, "0")}-${String(renewalExpiry.getDate()).padStart(2, "0")}`;
        return (
            <Card key={edition} shadow="sm" radius="md" padding="md" style={{ background: "#2a1414" }}>
                <Stack gap="xs">
                    <Group justify="space-between" align="center">
                        <Title order={4}>{editionLabel(edition)}</Title>
                        <Badge variant="light" color={edition === "pro" ? "lmuTVColors" : "gray"}>
                            {edition === "pro" ? t("cart.tier.pro") : t("cart.tier.standard")}
                        </Badge>
                    </Group>

                    {renewalExpiry && (
                        <Alert
                            color="lmuTVColors"
                            variant="light"
                            radius="sm"
                            p="xs"
                            icon={<IconRefresh size={16} />}
                        >
                            <Group justify="space-between" wrap="nowrap" gap="xs">
                                <Text size="xs" style={{ flex: 1 }}>
                                    {t("cart.renewal.body", { date: renewalDateStr })}
                                </Text>
                                <Button
                                    size="compact-xs"
                                    variant={startsAtMatchesRenewal ? "filled" : "light"}
                                    color="lmuTVColors"
                                    onClick={() => this.applyRenewalDate(edition)}
                                    disabled={startsAtMatchesRenewal}
                                >
                                    {startsAtMatchesRenewal ? t("cart.renewal.applied") : t("cart.renewal.apply")}
                                </Button>
                            </Group>
                        </Alert>
                    )}

                    <Group gap={4} align="center">
                        <Text size="xs" c="dimmed">
                            {t("cart.duration")}
                        </Text>
                        <InfoHint label={t("cart.hint.duration")} />
                    </Group>
                    <SegmentedControl
                        fullWidth
                        size="xs"
                        value={sel.duration}
                        onChange={(v) => this.setEditionSelection(edition, { duration: v as Duration })}
                        data={DURATIONS.map((d) => ({
                            label: t(`cart.durations.${d}`),
                            value: d,
                        }))}
                    />

                    <Group grow align="end">
                        <NumberInput
                            label={
                                <Group gap={4} align="center" component="span">
                                    {t("cart.pcs")}
                                    <InfoHint label={t("cart.hint.pcs")} />
                                </Group>
                            }
                            min={1}
                            max={50}
                            value={sel.pcs}
                            onChange={(v) =>
                                this.setEditionSelection(edition, {
                                    pcs: typeof v === "number" ? v : parseInt(v as string, 10) || 1,
                                })
                            }
                            allowDecimal={false}
                            clampBehavior="strict"
                        />
                        <Stack gap={2}>
                            <Text size="xs" c="dimmed">
                                {t("cart.unit")}: {u.toFixed(2)}€
                            </Text>
                            <Text fw={700}>
                                {t("cart.subtotal")}: {linePreview.toFixed(2)}€
                            </Text>
                            <Text size="xs" c="dimmed">
                                {t("cart.validUntil")}:{" "}
                                <Text component="span" inherit c="lmuTVColors.4">
                                    {sel.duration === "lifetime"
                                        ? t("cart.validForever")
                                        : formatValidUntil(
                                              validUntilForDuration(sel.duration, parseISODate(sel.startsAt)),
                                              i18n.language
                                          )}
                                </Text>
                            </Text>
                            <Text
                                size="xs"
                                c="lmuTVColors.6"
                                style={{ visibility: showBulk ? "visible" : "hidden" }}
                            >
                                {t("cart.bulkNote")}
                            </Text>
                        </Stack>
                    </Group>

                    {/* Start-date picker — only for time-bound durations.
                        Slot reserved with min-height to prevent layout shift when duration toggles. */}
                    <div style={{ minHeight: 70 }}>
                        {sel.duration !== "lifetime" && (
                            <DateInput
                                size="xs"
                                label={t("cart.startsAt")}
                                leftSection={<IconCalendar size={14} />}
                                value={parseISODate(sel.startsAt)}
                                minDate={new Date()}
                                onChange={(d: any) => {
                                    if (!d) return;
                                    const dd = d instanceof Date ? d : new Date(d);
                                    this.setEditionSelection(edition, {
                                        startsAt: `${dd.getFullYear()}-${String(dd.getMonth() + 1).padStart(2, "0")}-${String(dd.getDate()).padStart(2, "0")}`,
                                    });
                                }}
                                valueFormat="DD.MM.YYYY"
                            />
                        )}
                    </div>

                    <Button
                        leftSection={<IconShoppingCartPlus size={16} />}
                        onClick={() => this.addCurrentToCart(edition)}
                    >
                        {t("cart.addToCart")}
                    </Button>
                </Stack>
            </Card>
        );
    }

    renderCartTable() {
        const { t } = this.props;
        const { cart } = this.state;
        if (cart.Items.length === 0) {
            return (
                <Text c="dimmed" size="sm" ta="center" py="md">
                    {t("cart.empty")}
                </Text>
            );
        }
        return (
            <Stack gap="xs">
                {cart.Items.map((l, idx) => {
                    const lineTotal = getLineTotal(l);
                    return (
                        <Group key={idx} justify="space-between" wrap="nowrap">
                            <Stack gap={0}>
                                <Text fw={600}>
                                    {editionLabel(l.edition)} · {t(`cart.durations.${l.duration}`)}
                                </Text>
                                <Text size="xs" c="dimmed">
                                    {unitPrice(l.edition, l.duration).toFixed(2)}€ × {l.pcs}
                                    {l.pcs > FULL_PRICE_PCS && ` (${t("cart.bulkNote")})`}
                                </Text>
                                {l.duration !== "lifetime" && l.startsAt && (
                                    <Text size="xs" c="dimmed">
                                        {t("cart.startsAt")}: {formatValidUntil(parseISODate(l.startsAt), i18n.language)}
                                    </Text>
                                )}
                                <Text size="xs" c="dimmed">
                                    {t("cart.validUntil")}:{" "}
                                    {l.duration === "lifetime"
                                        ? t("cart.validForever")
                                        : formatValidUntil(
                                              validUntilForDuration(l.duration, parseISODate(l.startsAt)),
                                              i18n.language
                                          )}
                                </Text>
                            </Stack>
                            <Group gap="xs" wrap="nowrap">
                                <NumberInput
                                    size="xs"
                                    w={70}
                                    min={1}
                                    max={50}
                                    value={l.pcs}
                                    onChange={(v) =>
                                        this.updateLinePcs(idx, typeof v === "number" ? v : parseInt(v as string, 10) || 1)
                                    }
                                    allowDecimal={false}
                                    clampBehavior="strict"
                                />
                                <Text fw={700} w={80} ta="right">
                                    {lineTotal.toFixed(2)}€
                                </Text>
                                <Button
                                    size="xs"
                                    variant="subtle"
                                    color="red"
                                    onClick={() => this.removeLine(idx)}
                                    aria-label={t("cart.remove")}
                                >
                                    <IconTrash size={14} />
                                </Button>
                            </Group>
                        </Group>
                    );
                })}
            </Stack>
        );
    }

    render() {
        const { t, isPending, logged_in } = this.props;
        const { cart, tosAccepted, accTvKey } = this.state;

        if (!logged_in) return <></>;
        if (isPending)
            return (
                <div className={classes.loaderContainer}>
                    <Loader size="xl" />
                </div>
            );

        const subtotal = cart.Total();
        const accTvKeyTrim = accTvKey.trim();
        const accTvValid = isValidAccTvKey(accTvKeyTrim);
        const accTvInvalid = accTvKeyTrim.length > 0 && !accTvValid;
        const discount = accTvValid ? subtotal * ACC_TV_DISCOUNT : 0;
        const total = subtotal - discount;
        const empty = cart.Items.length === 0;

        return (
            <>
                <Modal
                    className={classes.errorModal}
                    opened={this.state.showPreError || this.state.showPostError}
                    onClose={() => this.setState({ showPostError: false, showPreError: false })}
                    title={t("cart.error.title")}
                    size="lg"
                    centered
                    closeOnClickOutside={false}
                    closeOnEscape={false}
                    withCloseButton={false}
                >
                    {this.state.showPreError && <>{t("cart.error.preOrderError")}</>}
                    {this.state.showPostError && (
                        <>
                            {t("cart.error.postOrderError")} <b>{this.state.orderID}</b>
                        </>
                    )}
                    <Button
                        onClick={() => {
                            this.props.loadSessionDataAsync();
                            this.setState({ showPostError: false, showPreError: false });
                        }}
                        fullWidth
                        mt="md"
                        variant="outline"
                    >
                        {t("cart.error.close")}
                    </Button>
                </Modal>

                <div className={classes.checkoutContainer}>
                    <Stack gap="md">
                        <Group grow align="stretch">
                            {EDITIONS.map((e) => this.renderEditionCard(e))}
                        </Group>

                        <Text fw={700} size="sm" tt="uppercase" c="dimmed" mt="md">
                            {t("cart.summary")}
                        </Text>

                        {this.renderCartTable()}

                        {/* ACC TV Lifetime holder discount */}
                        <TextInput
                            label={t("cart.accTvKey.label")}
                            description={t("cart.accTvKey.hint")}
                            placeholder="00000000-0000-0000-0000-000000000000"
                            leftSection={<IconKey size={14} />}
                            value={accTvKey}
                            onChange={(e) => this.setState({ accTvKey: e.currentTarget.value })}
                            error={accTvInvalid ? t("cart.accTvKey.invalid") : undefined}
                            rightSection={
                                accTvValid ? (
                                    <Text size="xs" c="lmuTVColors.4" pr="sm" fw={600}>
                                        −15%
                                    </Text>
                                ) : null
                            }
                        />

                        {accTvValid && (
                            <Group justify="space-between">
                                <Text size="sm" c="dimmed">
                                    {t("cart.subtotal")}
                                </Text>
                                <Text size="sm" c="dimmed">
                                    {subtotal.toFixed(2)}€
                                </Text>
                            </Group>
                        )}
                        {accTvValid && (
                            <Group justify="space-between">
                                <Text size="sm" c="lmuTVColors.4">
                                    {t("cart.accTvKey.discountLine")}
                                </Text>
                                <Text size="sm" c="lmuTVColors.4">
                                    −{discount.toFixed(2)}€
                                </Text>
                            </Group>
                        )}

                        <Group justify="space-between" pt="xs">
                            <Text fw={700}>{t("cart.total")}</Text>
                            <Text fw={700} size="xl">
                                {total.toFixed(2)}€
                            </Text>
                        </Group>

                        <Checkbox
                            checked={tosAccepted}
                            disabled={empty}
                            onChange={() => this.setState({ tosAccepted: !tosAccepted })}
                            label={
                                <Text size="xs">
                                    {t("cart.tosCheckbox.prefix")}{" "}
                                    <Anchor href="/tos" target="_blank" size="xs">
                                        {t("cart.tosCheckbox.tos")}
                                    </Anchor>{" "}
                                    {t("cart.tosCheckbox.and")}{" "}
                                    <Anchor href="/privacy_web" target="_blank" size="xs">
                                        {t("cart.tosCheckbox.privacy")}
                                    </Anchor>
                                </Text>
                            }
                        />

                        <div style={{ position: "relative" }}>
                            <PayPalButtons
                                style={{ layout: "vertical", color: "black", shape: "rect" }}
                                disabled={empty || !tosAccepted}
                                createOrder={this.createOrder}
                                onApprove={this.onApprove}
                                onError={this.onError}
                            />
                            {(empty || !tosAccepted) && (
                                <div
                                    style={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backdropFilter: "blur(3px)",
                                        backgroundColor: "rgba(0,0,0,0.15)",
                                        borderRadius: 4,
                                        cursor: "not-allowed",
                                        zIndex: 9999,
                                    }}
                                />
                            )}
                        </div>
                    </Stack>
                </div>
            </>
        );
    }
}

export const Checkout = ({ logged_in }: CheckoutProps) => {
    const { closeCart, pendingSelection } = useCart();
    const navigate = useNavigate();
    const { t } = useTranslation("translation");
    const { loadSessionDataAsync } = useSession();
    const [{ isPending }] = usePayPalScriptReducer();

    const redirectToProfile = () => {
        closeCart();
        navigate("/account");
    };

    const initial = useMemo(
        () => pendingSelection ?? { edition: "standard" as Edition, duration: "lifetime" as Duration, pcs: 1, autoAdd: false },
        [pendingSelection]
    );

    return (
        <CheckoutInner
            logged_in={logged_in}
            isPending={isPending}
            onApproveNavigation={redirectToProfile}
            // react-i18next's TFunction has a union return type; bridge it to the
            // simple (key, opts) => string prop the class component expects.
            t={t as unknown as (key: string, opts?: any) => string}
            loadSessionDataAsync={loadSessionDataAsync}
            initialSelection={initial}
        />
    );
};
